import { Component } from "react";
import { compose } from "recompose";
import { Flex, Box } from "@chakra-ui/react";
import { withTranslation } from "react-i18next";
import environment, { getAppValue } from "src/utils/environment";
import Menu from "src/components/Menu";
import MenuItem from "src/components/Menu/Item";
import MenuItemText from "src/components/Menu/ItemText";
import MotionDetectionSettings from "src/modules/motion-detection/MotionDetectionSettings";
import NoiseSensitivitySettings from "./NoiseSensitivitySettings";
import SchedulerSettings from "./SchedulerSettings";
import { ReactComponent as MicrophoneIcon } from "./icons/microphone-outlined.svg";
import { ReactComponent as MotionBaby } from "./icons/motion-baby.svg";
import { ReactComponent as MotionDog } from "./icons/motion-dog.svg";
import { ReactComponent as SchedulerIcon } from "src/icons/clock.svg";
import { DarkMode } from "src/styling/Modes";
import { colorMap } from "src/modules/colors";

class Settings extends Component {
  state = {
    renderNoiseSensitivitySettings: false,
    renderScheduler: false
  };
  settings = {
    noiseSensitivity: {
      label: "noiseSensitivity.heading",
      onClick: () => this.toggleNoiseSettings(),
      Icon: MicrophoneIcon,
      iconFill: colorMap.noiseSensitivity
    },
    motionSensitivity: {
      label: "motionDetection.heading",
      onClick: () => this.props.toggleMotionSettings(),
      Icon: getAppValue({ bibino: MotionBaby, barkio: MotionDog }),
      iconFill: colorMap.motionDetection
    },
    ...(environment.isBarkio && {
      scheduler: {
        label: "scheduler.heading",
        onClick: () => this.toggleScheduler(),
        Icon: SchedulerIcon,
        iconFill: colorMap.scheduler
      }
    })
  };

  toggleNoiseSettings = () => {
    const { onClose } = this.props;
    this.setState(({ renderNoiseSensitivitySettings }) => ({
      renderNoiseSensitivitySettings: !renderNoiseSensitivitySettings
    }));
    onClose();
  };

  toggleScheduler = () => {
    const { onClose } = this.props;
    this.setState(({ renderScheduler }) => ({
      renderScheduler: !renderScheduler
    }));
    onClose();
  };

  componentDidUpdate = (prevProps, prevState) => {
    const { renderScheduler } = this.state;
    const { scheduler, openStartScheduledMonitoringDialog, isConnected } =
      this.props;
    if (
      !scheduler ||
      !scheduler?.isEnabled ||
      prevState.renderScheduler === renderScheduler
    ) {
      return;
    }
    if (renderScheduler) scheduler.ignoreTaskStart = true;
    else {
      scheduler.ignoreTaskStart = false;
      scheduler.checkTasks();
      if (scheduler?.hasActiveTask && !isConnected) {
        openStartScheduledMonitoringDialog();
      }
    }
  };

  componentWillUnmount = () => {
    const { scheduler } = this.props;
    if (scheduler) scheduler.ignoreTaskStart = false;
  };

  render() {
    const { renderNoiseSensitivitySettings, renderScheduler } = this.state;

    const {
      isOpen,
      onClose,
      isConnected,
      renderMotionSensitivitySettings,
      toggleMotionSettings,
      scheduler,
      t
    } = this.props;
    return (
      <>
        <SchedulerSettings
          isOpen={renderScheduler}
          onClose={this.toggleScheduler}
          scheduler={scheduler}
        />
        <NoiseSensitivitySettings
          isOpen={renderNoiseSensitivitySettings}
          onClose={this.toggleNoiseSettings}
        />
        <MotionDetectionSettings
          isOpen={renderMotionSensitivitySettings}
          onClose={toggleMotionSettings}
        />

        <DarkMode adjuster={(colors) => ({ surfaceElevated1: colors.black70 })}>
          <Menu
            popoverProps={
              isConnected && {
                offset: [90, 10]
              }
            }
            placement={`top-${isConnected ? "start" : "end"}`}
            anchorEl={
              <Box
                position="fixed"
                top="10px"
                {...(isConnected ? { left: "90px" } : { right: "12px" })}
              />
            }
            isOpen={isOpen}
            onClose={onClose}
          >
            {Object.values(this.settings).map(
              ({ Icon, label, onClick, iconFill }, i) => {
                const isLast = i === Object.keys(this.settings).length - 1;
                const isFirst = i === 0;
                return (
                  <MenuItem
                    mt={isFirst ? "8px" : "0px"}
                    mb={isLast ? "8px" : "0px"}
                    px="16px"
                    key={label}
                    onClick={onClick}
                  >
                    <Flex
                      alignItems="center"
                      justifyContent="space-between"
                      width="100%"
                      maxWidth="203px"
                    >
                      <Box
                        mr="16px"
                        sx={{
                          svg: { fill: iconFill }
                        }}
                      >
                        <Icon />
                      </Box>
                      <MenuItemText
                        mr="0px !important"
                        whiteSpace="nowrap"
                        overflow="hidden"
                        textOverflow="ellipsis"
                        maxWidth="150px"
                      >
                        {t(label)}
                      </MenuItemText>
                    </Flex>
                  </MenuItem>
                );
              }
            )}
          </Menu>
        </DarkMode>
      </>
    );
  }
}

const enhance = compose(withTranslation());

export default enhance(Settings);
